import type {
  ConflictDetail,
  FieldInputResolution,
  RegistryResolver,
  ResolvedFieldRecord,
  ResolverRegistryId,
  SourceRef,
  TierSource,
} from './types';

export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a === 'object' && typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Reconcile tier sources for a single field. Lower tier number wins.
 * Surfaces conflicts when non-winning tiers disagree with the winner.
 */
export function reconcileTierSources(
  fieldKey: string,
  sources: TierSource[],
  nowIso?: string,
): FieldInputResolution {
  const resolvedAt = nowIso ?? new Date().toISOString();
  const present = sources.filter((s) => s.value !== undefined && s.value !== null);

  if (present.length === 0) {
    return {
      resolvedValue: null,
      resolvedFromTier: 3,
      sources: [],
      resolvedAt,
    };
  }

  const sorted = [...present].sort((a, b) => a.tier - b.tier || a.source.ref.localeCompare(b.source.ref));
  const winner = sorted[0]!;
  const conflicts: ConflictDetail[] = [];

  for (const src of sorted) {
    if (src === winner) continue;
    if (!valuesEqual(winner.value, src.value)) {
      conflicts.push({
        fieldKey,
        tier: src.tier,
        sources: [src.source],
        message: `Tier-${src.tier} source disagrees with tier-${winner.tier} winner`,
      });
    }
  }

  return {
    resolvedValue: winner.value,
    resolvedFromTier: winner.tier,
    sources: sorted.map((s) => s.source),
    conflicts: conflicts.length > 0 ? conflicts : undefined,
    resolvedAt,
  };
}

export interface CreateRegistryResolverArgs {
  registryId: ResolverRegistryId;
  siteId: string;
  fieldSources: Record<string, TierSource[]>;
  now?: () => number;
}

export function createRegistryResolver(args: CreateRegistryResolverArgs): RegistryResolver {
  const nowFn = args.now ?? (() => Date.now());

  return {
    registryId: args.registryId,
    siteId: args.siteId,

    resolveField(fieldKey: string): FieldInputResolution | null {
      const sources = args.fieldSources[fieldKey];
      if (!sources) return null;
      return reconcileTierSources(fieldKey, sources, new Date(nowFn()).toISOString());
    },

    resolveAll(): Record<string, FieldInputResolution> {
      const out: Record<string, FieldInputResolution> = {};
      for (const [fieldKey, sources] of Object.entries(args.fieldSources)) {
        out[fieldKey] = reconcileTierSources(fieldKey, sources, new Date(nowFn()).toISOString());
      }
      return out;
    },

    toResolvedRecords(): ResolvedFieldRecord[] {
      return Object.entries(this.resolveAll()).map(([fieldKey, resolution]) => ({
        fieldKey,
        registryId: args.registryId,
        siteId: args.siteId,
        resolvedValue: resolution.resolvedValue,
        resolvedFromTier: resolution.resolvedFromTier,
        sources: resolution.sources,
        conflicts: resolution.conflicts,
        lastResolvedAt: resolution.resolvedAt,
        conflictCount: resolution.conflicts?.length ?? 0,
      }));
    },
  };
}

export function tierSource(
  tier: 1 | 2 | 3,
  value: unknown,
  source: Omit<SourceRef, 'tier'>,
): TierSource {
  return { tier, value, source: { ...source, tier } };
}
